import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    modifyGeneralScheduleTimeAPI,
    modifyOneOnOneScheduleTimeAPI,
    ScheduleWriteResult,
} from '../../api/schedule';
import { normalizeSmartScheduleData } from '../smartSchedule/normalize';
import type { SmartScheduleEvent } from '../smartSchedule/types';
import { buildQuickMoveSaveNotice, resolveQuickMoveError } from './errors';
import {
    buildGeneralQuickMoveBody,
    buildPrivateQuickMoveBody,
    getQuickMoveVerificationDates,
    isQuickMovePlanUnchanged,
    verifyQuickMoveApplied,
} from './plan';
import {
    fetchSmartRangeSchedules,
    QUICK_MOVE_INVALIDATION_KEYS,
    SMART_RANGE_SCHEDULES_QUERY_KEY,
    smartRangeSchedulesQueryKey,
} from './queries';
import { QuickMoveRefetchState, QuickMoveSaveNotice, QuickMoveSavePlan, QuickMoveSaveResult, QuickMoveVerification } from './types';

export interface UseQuickMoveSaveParams {
    centerId: string;
    authorization: string;
    /** 시트를 다시 열었을 때도 결과 미확인 안내를 유지한다. */
    scheduleId?: string;
}

export interface UseQuickMoveSaveReturn {
    /** 저장 → 무효화 → 재조회(await) → 실제 상태 판정까지 끝난 뒤 결과를 돌려준다. 던지지 않는다. */
    save: (plan: QuickMoveSavePlan) => Promise<QuickMoveSaveResult>;
    /** 요청~재조회가 끝날 때까지 true. */
    isSaving: boolean;
    heldNotice?: QuickMoveSaveNotice;
    heldPlan?: QuickMoveSavePlan;
    /** 보관된 결과의 반영 관측값. 다시 연 시트가 자기 일정 겹침 제외 여부를 정할 때 쓴다. */
    heldVerification?: QuickMoveVerification;
}

type SavedResult = Extract<QuickMoveSaveResult, { type: 'success' | 'failure' }>;
type HeldOutcome = { pending: boolean; result?: SavedResult; plan?: QuickMoveSavePlan };
const outcomeKey = (centerId: string, scheduleId: string) => ['quickMoveUnconfirmedOutcome', centerId, scheduleId];
const PENDING_NOTICE: QuickMoveSaveNotice = {
    tone: 'warning', title: '변경 결과를 확인하고 있어요',
    message: '이 일정은 다른 화면에서 변경 처리 중이에요. 잠시 기다려주세요.', showRefresh: false,
};

const sendQuickMove = (plan: QuickMoveSavePlan, centerId: string, authorization: string): Promise<ScheduleWriteResult> =>
    plan.source.kind === 'private'
        ? modifyOneOnOneScheduleTimeAPI(centerId, plan.source.scheduleId, buildPrivateQuickMoveBody(plan), authorization)
        : modifyGeneralScheduleTimeAPI(centerId, plan.source.scheduleId, buildGeneralQuickMoveBody(plan), authorization);

/**
 * 빠른 변경 저장 훅(§3 저장·저장 후).
 *
 * - 원래와 같으면 요청 없이 `unchanged`.
 * - 요청 중 중복 실행은 ref 로 막는다(`busy`) — 상태값은 렌더 전까지 갱신되지 않아 더블클릭을 못 막는다.
 * - 성공·실패 모두 기존 수정 폼과 같은 쿼리 묶음을 무효화하고, Smart 쿼리 재조회를 **await** 한다.
 *   기존 scheduleWriteCache 헬퍼는 재조회 실패를 삼키므로 쓰지 않는다. refetch 오류는
 *   `throwOnError: true` 로만 전파된다(react-query v4 기본값은 삼킴).
 * - 원래 날·목적지 날을 staleTime 0 으로 다시 읽어 실제 반영 여부를 판정한다
 *   (전역 기본 staleTime 1분 때문에 지정하지 않으면 캐시를 돌려준다).
 */
export const useQuickMoveSave = ({ centerId, authorization, scheduleId }: UseQuickMoveSaveParams): UseQuickMoveSaveReturn => {
    const queryClient = useQueryClient();
    const heldOutcome = useQuery<HeldOutcome>({
        queryKey: outcomeKey(centerId, scheduleId || ''), enabled: false,
        cacheTime: Infinity, staleTime: Infinity,
    });
    const inFlightRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);

    const mutation = useMutation({
        mutationFn: async (plan: QuickMoveSavePlan): Promise<ScheduleWriteResult> => {
            try {
                return await sendQuickMove(plan, centerId, authorization);
            } catch (error: any) {
                // API 함수는 던지지 않지만, 혹시 던지면 결과 미확인(0)으로 다룬다.
                return { ok: false, status: 0, errorCode: '', message: error?.message || '', data: undefined };
            }
        },
    });
    const { mutateAsync } = mutation;

    const refetchAndVerify = useCallback(
        async (plan: QuickMoveSavePlan): Promise<{ refetch: QuickMoveRefetchState; verification: QuickMoveVerification }> => {
            // 다른 화면용 쿼리는 기존 폼처럼 무효화만 한다(결과를 기다리지 않는다).
            QUICK_MOVE_INVALIDATION_KEYS[plan.source.kind]
                .filter((key) => key !== SMART_RANGE_SCHEDULES_QUERY_KEY)
                .forEach((key) => {
                    void queryClient.invalidateQueries({ queryKey: [key] });
                });

            let refetch: QuickMoveRefetchState = 'ok';
            try {
                await queryClient.invalidateQueries(
                    { queryKey: [SMART_RANGE_SCHEDULES_QUERY_KEY] },
                    { throwOnError: true }
                );
            } catch {
                refetch = 'failed';
            }

            let verification: QuickMoveVerification = 'unknown';
            try {
                const dayResults = await Promise.all(
                    getQuickMoveVerificationDates(plan).map((date) =>
                        queryClient.fetchQuery({
                            queryKey: smartRangeSchedulesQueryKey(centerId, date, date),
                            queryFn: () => fetchSmartRangeSchedules(centerId, date, date, authorization),
                            staleTime: 0,
                        })
                    )
                );
                const events: SmartScheduleEvent[] = normalizeSmartScheduleData({
                    rangeSchedules: dayResults.flat(),
                }).events;
                verification = verifyQuickMoveApplied(plan, events);
            } catch {
                refetch = 'failed';
                verification = 'unknown';
            }

            return { refetch, verification };
        },
        [authorization, centerId, queryClient]
    );

    const save = useCallback(
        async (plan: QuickMoveSavePlan): Promise<QuickMoveSaveResult> => {
            if (inFlightRef.current) return { type: 'busy' };
            if (isQuickMovePlanUnchanged(plan)) return { type: 'unchanged' };
            const key = outcomeKey(centerId, plan.source.scheduleId);
            const held = queryClient.getQueryData<HeldOutcome>(key);
            if (held?.pending) return { type: 'busy' };
            if (held?.result) return held.result;

            inFlightRef.current = true;
            setIsSaving(true);
            queryClient.setQueryDefaults(key, { cacheTime: Infinity, staleTime: Infinity });
            queryClient.setQueryData<HeldOutcome>(key, { pending: true, plan });
            try {
                const result = await mutateAsync(plan);
                const error = result.ok ? undefined : resolveQuickMoveError(result, plan.source.kind);
                const { refetch, verification } = await refetchAndVerify(plan);
                const outcome: SavedResult = {
                    type: result.ok ? 'success' : 'failure',
                    status: result.status,
                    error,
                    refetch,
                    verification,
                    notice: buildQuickMoveSaveNotice({ requestOk: result.ok, error, refetch, verification }),
                    ...(result.staleGuardSkipped ? { staleGuardSkipped: true } : {}),
                };
                if (result.staleGuardSkipped) {
                    // 구서버(배포 중 구 worker·롤백) 대비 재전송 — 화면 문구는 그대로, 흔적만 남긴다.
                    console.warn('[quickMove] 서버가 expected* 를 몰라 stale 보호 없이 재전송했습니다.', {
                        scheduleId: plan.source.scheduleId,
                        status: result.status,
                    });
                }
                if (error?.outcomeUnknown || outcome.notice.showRefresh) {
                    // 모달·페이지 수명 밖에서도 재요청을 막는다. 명시적 전체 새로고침 후 다시 확인한다.
                    queryClient.setQueryData<HeldOutcome>(key, { pending: false, result: outcome, plan });
                } else queryClient.setQueryData<HeldOutcome>(key, { pending: false });
                return outcome;
            } catch (unexpected: any) {
                const error = resolveQuickMoveError({ status: 0, message: unexpected?.message || '' }, plan.source.kind);
                const outcome: SavedResult = {
                    type: 'failure', status: 0, error, refetch: 'failed', verification: 'unknown',
                    notice: buildQuickMoveSaveNotice({ requestOk: false, error, refetch: 'failed', verification: 'unknown' }),
                };
                queryClient.setQueryData<HeldOutcome>(key, { pending: false, result: outcome, plan });
                return outcome;
            } finally {
                inFlightRef.current = false;
                setIsSaving(false);
            }
        },
        [centerId, mutateAsync, queryClient, refetchAndVerify]
    );

    return { save, isSaving, heldNotice: heldOutcome.data?.pending ? PENDING_NOTICE : heldOutcome.data?.result?.notice,
        heldPlan: heldOutcome.data?.result ? heldOutcome.data.plan : undefined,
        heldVerification: heldOutcome.data?.result?.verification };
};
